import { Mail, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useCms } from "@/lib/cms";
import { FOOTER_EXPLORE_LINKS, FOOTER_SERVICE_LINKS } from "@/lib/publicNav";
import { useMatchMedia } from "@/lib/useMatchMedia";
import { MOBILE_HERO_MAX_WIDTH } from "@/components/sections/hero/heroShared";
import SiteBrandRow from "@/components/SiteBrandRow";

export default function Footer() {
  const { cms } = useCms();
  const isMobile = useMatchMedia(`(max-width: ${MOBILE_HERO_MAX_WIDTH}px)`);
  const email = cms?.company_email || "dsitesolution@dsitesolution.com";
  const kakaoUrl = cms?.kakao_channel_url || "https://pf.kakao.com/_rmpFn/chat";
  const phone = cms?.company_phone || "";
  const address = cms?.company_address || "";
  const businessNo = cms?.company_business_no || "646-01-03718";
  const ceoName = cms?.company_ceo || "한나래";
  const copyright =
    cms?.footer_copyright ||
    `© ${new Date().getFullYear()} DSiteSolution. All rights reserved.`;
  return (
    <footer
      data-testid="site-footer"
      className="ds-site-footer border-t border-white/10 bg-[#050505] relative z-30"
    >
      <div className="ds-site-footer-main max-w-7xl mx-auto py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 min-w-0">
        <div className="md:col-span-5 min-w-0">
          <div className="ds-site-footer__brand flex items-center gap-3 mb-4 flex-wrap">
            <SiteBrandRow
              cms={cms}
              size="sm"
              iconOnly={Boolean(isMobile)}
              logoTestId="footer-logo-image"
              wordmarkTestId="footer-wordmark-image"
            />
          </div>
          <p className="text-sm text-white/60 max-w-sm leading-relaxed">
            브랜드 웹사이트부터 맞춤형 개발, 관리자 시스템, AI와 자동화까지 하나의 디지털 시스템으로 구축합니다.
          </p>
        </div>

        <div className="md:col-span-3 min-w-0">
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-4">
            서비스
          </h4>
          <ul className="space-y-2 text-sm text-white/70">
            {FOOTER_SERVICE_LINKS.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className="hover:text-white transition-colors">{item.label}</Link>
              </li>
            ))}
          </ul>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-3 mt-6">
            탐색
          </h4>
          <ul className="space-y-2 text-sm text-white/70">
            {FOOTER_EXPLORE_LINKS.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className="hover:text-white transition-colors">{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4 min-w-0">
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-4">
            문의
          </h4>
          <div className="space-y-3 text-sm text-white/70">
            <a
              href={`mailto:${email}`}
              data-testid="footer-email"
              className="flex items-center gap-2 hover:text-white transition-colors break-all"
            >
              <Mail size={14} className="shrink-0" /> {email}
            </a>
            {phone && (
              <div
                data-testid="footer-phone"
                className="flex items-center gap-2 text-white/70"
              >
                <span className="text-white/40 text-xs uppercase tracking-widest">TEL</span>
                {phone}
              </div>
            )}
            <a
              href={kakaoUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-kakao"
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <MessageCircle size={14} className="shrink-0" /> 카카오톡 채널 상담
            </a>
            {address && (
              <div
                data-testid="footer-address"
                className="text-xs text-white/50 leading-relaxed pt-1"
              >
                {address}
              </div>
            )}
            <div
              data-testid="footer-business-no"
              className="text-xs text-white/50"
            >
              사업자등록번호 {businessNo} · 대표: {ceoName}
            </div>
          </div>
        </div>
      </div>
      <div
        data-testid="footer-bottom-bar"
        className="ds-site-footer-bar border-t border-white/5 py-5 md:py-6 text-xs text-white/40 max-w-7xl mx-auto min-w-0"
      >
        <span data-testid="footer-copyright" className="min-w-0 break-words">
          {copyright}
        </span>
      </div>
    </footer>
  );
}
